import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaSearch, FaCalendarAlt, FaFileInvoice, FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { api } from '../services/api';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #2c3e50;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.$color || '#2c3e50'};
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-top: 0.25rem;
`;

const FiltersContainer = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  flex: 1;
  min-width: 200px;

  input {
    border: none;
    background: none;
    outline: none;
    flex: 1;
    font-size: 1rem;
    margin-left: 0.5rem;
  }
`;

const DateFilter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #e9ecef;
  }
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: top;
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const ExpandableRow = styled.tr`
  background: #f8f9fa;
  display: ${props => props.$expanded ? 'table-row' : 'none'};
`;

const LinesContainer = styled.div`
  padding: 1rem;
`;

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #ddd;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$invoiced ? '#27ae60' : '#95a5a6'};
  color: white;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #3498db;
  padding: 0.25rem;
  font-size: 1rem;

  &:hover {
    color: #2980b9;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: ${props => props.$active ? '#3498db' : 'white'};
  color: ${props => props.$active ? 'white' : '#2c3e50'};
  border-radius: 4px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#2980b9' : '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  overflow: hidden;
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  background: ${props => props.$expanded ? '#f8f9fa' : 'white'};
`;

const MobileCardContent = styled.div`
  padding: 1rem;
  border-top: 1px solid #e9ecef;
  display: ${props => props.$expanded ? 'block' : 'none'};
`;

const MobileLabel = styled.span`
  font-size: 0.8rem;
  color: #7f8c8d;
`;

const MobileValue = styled.div`
  font-weight: 500;
`;

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`;

const MobileOnly = styled.div`
  display: block;
  @media (min-width: 768px) {
    display: none;
  }
`;

const HistoricalOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortBy, setSortBy] = useState('originalCreatedAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/historical-orders/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit: 25,
        sortBy,
        sortOrder
      });

      if (search) params.append('search', search);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/historical-orders?${params}`);
      setOrders(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error al cargar pedidos históricos');
      console.error('Error fetching historical orders:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, startDate, endDate, sortBy, sortOrder]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  return (
    <Container>
      <Header>
        <Title>Pedidos Históricos</Title>
      </Header>

      {stats && (
        <StatsContainer>
          <StatCard>
            <StatValue $color="#3498db">{stats.totalOrders?.toLocaleString()}</StatValue>
            <StatLabel>Total Pedidos</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#27ae60">{stats.totalInvoiced?.toLocaleString()}</StatValue>
            <StatLabel>Facturados</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#e67e22">{stats.totalPending?.toLocaleString()}</StatValue>
            <StatLabel>Pendientes</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue $color="#2c3e50">{formatCurrency(stats.totalAmount)}</StatValue>
            <StatLabel>Importe Total</StatLabel>
          </StatCard>
        </StatsContainer>
      )}

      <FiltersContainer>
        <form onSubmit={handleSearch} style={{ display: 'flex', flex: 1, gap: '1rem', flexWrap: 'wrap' }}>
          <SearchInput>
            <FaSearch color="#7f8c8d" />
            <input
              type="text"
              placeholder="Buscar por cliente, factura..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInput>
          <DateFilter>
            <FaCalendarAlt color="#7f8c8d" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Desde"
            />
            <span>-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="Hasta"
            />
          </DateFilter>
        </form>
      </FiltersContainer>

      {loading ? (
        <EmptyState>Cargando...</EmptyState>
      ) : orders.length === 0 ? (
        <EmptyState>No se encontraron pedidos históricos</EmptyState>
      ) : (
        <>
          {/* Desktop Table */}
          <DesktopOnly>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <Th onClick={() => handleSort('legacyOrderId')}>
                      # {sortBy === 'legacyOrderId' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </Th>
                    <Th onClick={() => handleSort('originalCreatedAt')}>
                      Fecha {sortBy === 'originalCreatedAt' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </Th>
                    <Th onClick={() => handleSort('clientName')}>
                      Cliente {sortBy === 'clientName' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </Th>
                    <Th onClick={() => handleSort('totalAmount')}>
                      Importe {sortBy === 'totalAmount' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </Th>
                    <Th>Estado</Th>
                    <Th>Líneas</Th>
                    <Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <React.Fragment key={order.id}>
                      <Tr>
                        <Td>{order.legacyOrderId}</Td>
                        <Td>{formatDate(order.originalCreatedAt)}</Td>
                        <Td>
                          <strong>{order.clientName}</strong>
                          {order.client?.phone && (
                            <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                              {order.client.phone}
                            </div>
                          )}
                        </Td>
                        <Td style={{ fontWeight: 600 }}>{formatCurrency(order.totalAmount)}</Td>
                        <Td>
                          <Badge $invoiced={order.isInvoiced}>
                            {order.isInvoiced ? (
                              <><FaFileInvoice /> Facturado</>
                            ) : 'Pendiente'}
                          </Badge>
                          {order.invoiceReference && (
                            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                              Ref: {order.invoiceReference}
                            </div>
                          )}
                        </Td>
                        <Td>{order.lines?.length || 0}</Td>
                        <Td>
                          <ActionButton onClick={() => toggleRow(order.id)} title="Ver detalles">
                            {expandedRows.has(order.id) ? <FaChevronUp /> : <FaChevronDown />}
                          </ActionButton>
                        </Td>
                      </Tr>
                      <ExpandableRow $expanded={expandedRows.has(order.id)}>
                        <Td colSpan={7}>
                          <LinesContainer>
                            <strong>Líneas del pedido:</strong>
                            {order.lines?.length > 0 ? (
                              order.lines.map((line, idx) => (
                                <LineItem key={idx}>
                                  <span>
                                    {line.quantity}x {line.description || 'Sin descripción'}
                                  </span>
                                  <span>{formatCurrency(line.total)}</span>
                                </LineItem>
                              ))
                            ) : (
                              <p style={{ color: '#7f8c8d', margin: '0.5rem 0' }}>Sin líneas registradas</p>
                            )}
                            {order.deliveryDate && (
                              <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                                <strong>Fecha entrega prevista:</strong> {formatDate(order.deliveryDate)}
                              </div>
                            )}
                          </LinesContainer>
                        </Td>
                      </ExpandableRow>
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </DesktopOnly>

          {/* Mobile Cards */}
          <MobileOnly>
            {orders.map((order) => (
              <MobileCard key={order.id}>
                <MobileCardHeader
                  $expanded={expandedRows.has(order.id)}
                  onClick={() => toggleRow(order.id)}
                >
                  <div>
                    <MobileValue>#{order.legacyOrderId} - {order.clientName}</MobileValue>
                    <MobileLabel>{formatDate(order.originalCreatedAt)}</MobileLabel>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <MobileValue>{formatCurrency(order.totalAmount)}</MobileValue>
                    <Badge $invoiced={order.isInvoiced} style={{ marginTop: '0.25rem' }}>
                      {order.isInvoiced ? 'Facturado' : 'Pendiente'}
                    </Badge>
                  </div>
                </MobileCardHeader>
                <MobileCardContent $expanded={expandedRows.has(order.id)}>
                  <strong>Líneas ({order.lines?.length || 0}):</strong>
                  {order.lines?.length > 0 ? (
                    order.lines.map((line, idx) => (
                      <LineItem key={idx}>
                        <span>{line.quantity}x {line.description?.substring(0, 50) || 'Sin descripción'}</span>
                        <span>{formatCurrency(line.total)}</span>
                      </LineItem>
                    ))
                  ) : (
                    <p style={{ color: '#7f8c8d', margin: '0.5rem 0' }}>Sin líneas</p>
                  )}
                </MobileCardContent>
              </MobileCard>
            ))}
          </MobileOnly>

          {pagination && pagination.totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                Primera
              </PageButton>
              <PageButton
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </PageButton>
              <span>
                Página {page} de {pagination.totalPages} ({pagination.total} registros)
              </span>
              <PageButton
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
              >
                Siguiente
              </PageButton>
              <PageButton
                onClick={() => setPage(pagination.totalPages)}
                disabled={page === pagination.totalPages}
              >
                Última
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
};

export default HistoricalOrders;
